import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';

/**
 * Reusable admin search bar component
 * @param {Object} props
 * @param {string} props.value - Current search input value
 * @param {Function} props.onChange - Input change handler
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {Function} [props.onSubmit] - Form submit handler. If provided, renders as <form> with submit button
 * @param {Array} [props.searchTypes] - Array of { value, label } for type dropdown selector
 * @param {string} [props.selectedType] - Currently selected search type
 * @param {Function} [props.onTypeChange] - Search type change handler
 * @param {string} [props.submitLabel] - Submit button label text
 * @param {boolean} [props.submitting] - Whether a search is in progress (shows spinner)
 * @param {boolean} [props.submitDisabled] - Whether submit button is disabled
 * @param {React.ReactNode} [props.countBadge] - Count badge element to display after search
 * @param {React.ReactNode} [props.actions] - Extra action elements (e.g. create button)
 * @param {React.Ref} [props.inputRef] - Ref to attach to the search input
 */
export default function AdminSearchBar({
  value,
  onChange,
  placeholder,
  onSubmit,
  searchTypes,
  selectedType,
  onTypeChange,
  submitLabel,
  submitting,
  submitDisabled,
  countBadge,
  actions,
  inputRef,
}) {
  const isForm = !!onSubmit;
  const Wrapper = isForm ? 'form' : 'div';
  const wrapperProps = isForm ? { onSubmit } : {};

  return (
    <div className="header-actions">
      <Wrapper {...wrapperProps} className="search-box">
        {searchTypes && (
          <div className="search-type-selector">
            <select
              value={selectedType}
              onChange={onTypeChange}
              className="search-type-dropdown"
            >
              {searchTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        )}
        <FontAwesomeIcon icon={ICONS.SEARCH} />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
        {isForm && (
          <button type="submit" disabled={submitDisabled || submitting}>
            {submitting ? (
              <FontAwesomeIcon icon={ICONS.SPINNER} spin />
            ) : (
              submitLabel || 'Search'
            )}
          </button>
        )}
      </Wrapper>
      {countBadge}
      {actions}
    </div>
  );
}
